import { Component, OnInit, OnDestroy, signal } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { ApplicabilityService, NoteService, Store } from "@viewer/core";
import { TitleService } from "@viewer/header/title.service";
import { Subscription } from "rxjs";
import { Note, AttachedMediaFile, AttachedMedia } from "@orion2/models/tocitem.models";
import { IReactionDisposer, reaction, runInAction } from "mobx";
import { customFilterPredicate, decompressFile } from "@orion2/utils/front.utils";
import { HistorySource } from "@orion2/models/enums";
import { CsvService } from "@viewer/core/csv/csv.service";
import { DatePipe } from "@angular/common";
import { Charset, DownloadFileService } from "libs/download/download-file.service";
import { escapeSpecialCharactersForCSV, formatFilename } from "@orion2/utils/functions.utils";
import { MessageService } from "@orion2/message-service/message.service";
import { InterpolationParameters, TranslateService } from "@ngx-translate/core";
import { SelectionModel } from "@angular/cdk/collections";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult
} from "libs/components/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { CUSTOM_FILE_ERRORS } from "@orion2/utils/errors.utils";
import { CsvHeader } from "@viewer/shared-module/constant";

@Component({
  standalone: false,
  selector: "o-note-list",
  templateUrl: "./note-list.component.html",
  styleUrls: ["./note-list.component.scss"]
})
export class NoteListComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly HistorySource = HistorySource;
  public dataSource = new MatTableDataSource<Note>([]);
  public displayedColumns: string[];
  public itemType = "note";

  private notes: Note[] = [];
  private subscription: Subscription = new Subscription();
  public selectedNotes = new SelectionModel<string>(true, []);
  public selectMode = signal(false);
  public readonly warningFilterEnabled = signal(false);

  private itemPerPageDisposer: IReactionDisposer;

  constructor(
    public csvService: CsvService,
    public noteService: NoteService,
    public store: Store,
    private titleService: TitleService,
    private datePipe: DatePipe,
    private downloadService: DownloadFileService,
    private messageService: MessageService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private applicabilityService: ApplicabilityService
  ) {}

  ngOnInit(): void {
    this.titleService.setPageTitle("notes.label");
    this.displayedColumns = [this.itemType];

    runInAction(() => {
      // We need to reset currentDMC if we come from DMC to note-list.
      this.store.currentDMC = undefined;
      this.noteService.noteWarningIds.set(new Set());
    });

    this.subscription.add(
      this.noteService.tocItemsOfType.subscribe((data: Note[]) => {
        if (!data) {
          return this.noteService.refresh();
        }

        this.updateDataSource(data);
        this.noteService.setNotesWarningsIds();
      })
    );

    this.subscription.add(
      reaction(
        () => ({
          applicMd5: this.store.applicableMD5,
          show: this.store.showNotApplicable,
          isFilteringAvailable: this.store.isFilteringAvailable
        }),
        () => {
          this.updateDataSource(this.notes);
        },
        { fireImmediately: true }
      )
    );
    // unselect card on list and force combineLatest (toc.component)
    runInAction(() => {
      this.store.currentTocNode = {
        ...this.store.currentTocNode,
        unselected: true
      };
    });
    // Update the number of Note display on screen after setting
    this.itemPerPageDisposer = reaction(
      () => this.store.userItemsPerPage,
      (value: number) => {
        this.dataSource.paginator._changePageSize(value);
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.itemPerPageDisposer();
  }

  public async importNotes(event: Event, asAuthor = false): Promise<void> {
    const inputFile = (event.target as HTMLInputElement).files[0];
    (event.target as HTMLInputElement).value = "";

    try {
      // dezip -> csv + medias
      const unzip = await decompressFile(inputFile);
      // launch import
      await this.noteService.import(unzip, asAuthor);
    } catch (error) {
      let message: string;
      let params: InterpolationParameters;
      switch (error.message) {
        case CUSTOM_FILE_ERRORS.ZIP_ERROR:
          message = "error.file.format";
          break;
        case CUSTOM_FILE_ERRORS.CSV_ERROR:
          message = "notes.import.error.zip";
          params = { filename: inputFile.name };
          break;
        default:
          message = error.message;
          break;
      }

      this.messageService.error(this.translate.instant(message, params), undefined, 4000);
    }
  }

  public toggleSelection(noteId: string): void {
    this.selectedNotes.toggle(noteId);
    this.selectMode.set(this.selectedNotes.hasValue());
  }

  // if the method is called without argument, then uses this.notes
  // else first update this.notes with the value passed and then creates the datasource.
  public updateDataSource(notes: Note[] = this.notes): void {
    if (this.notes !== notes) {
      this.notes = notes;
    }

    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<Note>(notes);
      this.dataSource.filterPredicate = customFilterPredicate;
    }

    this.noteService.sortTocItemList(this.notes);
    // filter datasource if applicabilty filter hide some notes
    this.dataSource.data = this.notes.filter((note: Note) => {
      // public notes have not applicabilityMD5
      if (!note.applicabilityMD5) {
        note.applicabilityMD5 = this.store.getDUMeta(note.dmc)?.applicabilityMD5;
      }
      return this.store.showNotApplicable || this.applicabilityService.isApplicable(note);
    });
    this.filterOnNoteWithWarnings();
  }

  /**
   * This method checks if the notesInline capability is available in the store's publication info,
   * and applies the appropriate filtering based on the warning filter state.
   *
   * When warningFilterEnabled is true, it filters the data source to only include notes that have
   * warnings (notes whose IDs are present in noteService.noteWarningIds()).
   * When warningFilterEnabled is false, it resets the data source to show all notes.
   *
   * @private
   */
  private filterOnNoteWithWarnings(): void {
    if (!this.store.pubInfo.capabilities.notesInline) {
      return;
    }
    this.dataSource.data = this.warningFilterEnabled()
      ? this.dataSource.data.filter((note: Note) => this.noteService.noteWarningIds().has(note._id))
      : this.notes;
  }

  /**
   * Toggles the warning filter for notes in the list.
   * This method enables or disables filtering to show only notes with warnings.
   * The filter is only applied if the publication capability 'notesInline' is available.
   *
   * @public
   */
  public toggleWarningFilter(): void {
    if (!this.store.pubInfo.capabilities.notesInline) {
      return;
    }
    this.warningFilterEnabled.set(!this.warningFilterEnabled());
    this.filterOnNoteWithWarnings();
  }

  private setFormatFileName(creationDate: Date): string {
    return [
      "NOTES",
      this.store.publicationID,
      this.datePipe.transform(creationDate, "yyyy-MM-dd_HH_mm_ss")
    ].join("_");
  }

  private createCSV(selection = false): File {
    const date = new Date();
    const notesData = selection
      ? this.dataSource.data.filter((note: Note) => this.selectedNotes.isSelected(note._id))
      : this.dataSource.data;
    const csvHeaderTitles: string[] = [
      CsvHeader.ID,
      CsvHeader.TYPE,
      CsvHeader.DMC,
      CsvHeader.TITLE,
      CsvHeader.CONTENT,
      CsvHeader.CREATOR,
      CsvHeader.DATE_OF_CREATION,
      CsvHeader.REVISION
    ];

    if (this.noteService.canAttachMedia && notesData.some((note: Note) => note.medias?.length)) {
      csvHeaderTitles.push(CsvHeader.LINKED_MEDIA);
    }

    if (
      this.store.pubInfo?.capabilities?.notesInline &&
      notesData.some((note: Note) => note.anchor)
    ) {
      csvHeaderTitles.push(CsvHeader.ANCHOR_POINT);
    }

    const csvRowList = [
      csvHeaderTitles,
      ...notesData.map((note: Note, index: number) => {
        const row = [
          `note_${index + 1}`,
          note.status,
          note.dmc,
          `"${escapeSpecialCharactersForCSV(note.customTitle)}"`,
          `"${escapeSpecialCharactersForCSV(note.content)}"`,
          note.author,
          new Date(note.date).toISOString(),
          note.minRevision
        ] as string[];

        if (csvHeaderTitles.includes(CsvHeader.LINKED_MEDIA)) {
          row.push(
            note.medias?.map((media: AttachedMedia) => formatFilename(media.title)).join(", ") || ""
          );
        }

        if (csvHeaderTitles.includes(CsvHeader.ANCHOR_POINT)) {
          row.push(note.anchor ? JSON.stringify(note.anchor) : "");
        }
        return row;
      })
    ];
    const csvFilename: string = this.setFormatFileName(date);
    const csvString = this.csvService
      .createContentFromArray(csvRowList)
      .join(this.csvService.endOfLine);
    const blob = new Blob(["\uFEFF" + csvString], { type: Charset.CSV_UTF8 });
    return new File([blob], `${csvFilename}.csv`);
  }

  /**
   * Select or deselect all notes. The select all is applied on filtered notes.
   * @public
   * @returns {void}
   */
  public toggleAllSelection(): void {
    this.selectMode.set(!this.selectMode());
    const func = this.selectMode() ? "select" : "deselect";
    this.dataSource.filteredData.forEach((note: Note) => this.selectedNotes[func](note._id));
  }

  public exportAsZip(selection = false): Promise<void> {
    const zipFilename: string = this.setFormatFileName(new Date());
    const notesData: Note[] = selection
      ? this.dataSource.data.filter((note: Note) => this.selectedNotes.isSelected(note._id))
      : this.dataSource.data;

    if (!notesData.length) {
      return;
    }

    if (
      this.store.pubInfo.capabilities.notesInline &&
      notesData.some((note: Note) => this.noteService.noteWarningIds().has(note._id))
    ) {
      this.messageService.warning(this.translate.instant("notes.export.error.anchor.verification"));
    }

    const filesPromises = !this.noteService.canAttachMedia
      ? []
      : notesData.flatMap((note: Note, index: number) => {
          const folderName = `media_note_${index + 1}`;
          return note.medias.map((media: AttachedMedia) =>
            this.noteService
              .getAttachedMedia(media.id, `${folderName}/${media.title}`, note.status)
              .then((noteFile: AttachedMediaFile) => {
                const blob = "data" in noteFile.file ? noteFile.file.data : noteFile.file;
                return new File([blob], noteFile.file.name, {
                  type: noteFile.file.type
                });
              })
          );
        });

    return Promise.all(filesPromises).then((files: File[]) => {
      this.downloadService.download(
        files.concat(this.createCSV(selection)),
        `${zipFilename}`,
        null,
        true
      );
    });
  }

  /**
   * Deletes a selection of notes or all notes if deleteAll is set to true.
   * Calls deleteNotes for actual deletion.
   * @param deleteAll By default the user selection is to be deleted. If set to true, all notes are
   * deleted.
   */
  public deleteSelectedNotes(deleteAll = false): void {
    const selectedNotes = deleteAll
      ? this.notes
      : this.notes.filter((note: Note) => this.selectedNotes.isSelected(note._id));
    const data: ConfirmDialogData = {
      message: [
        this.translate.instant(
          deleteAll ? "notes.delete.all.confirmDiag" : "notes.delete.selection.confirmDiag"
        )
      ],
      confirmLabels: [this.translate.instant("continue")],
      cancelLabels: [this.translate.instant("cancel")]
    };
    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe(async (result: ConfirmDialogResult) => {
        if (result.confirm) {
          return await this.deleteAuthorizedNotes(selectedNotes);
        }
      });
  }

  /**
   * Deletes the notes given as parameter.
   * @param notesToDelete List of notes to delete.
   * @returns Promise of void when all notes are deleted.
   */
  private deleteAuthorizedNotes(notesToDelete: Note[]): Promise<void> {
    const deletableNotes = notesToDelete.filter((noteToDelete: Note) =>
      this.noteService.userHasRightForNoteStatus(noteToDelete.status)
    );
    const areUnauthorizedNotes = deletableNotes.length < notesToDelete.length;
    return Promise.all(
      deletableNotes.map((noteToDelete: Note) => this.noteService.deleteNote(noteToDelete, false))
    ).then(() => {
      if (areUnauthorizedNotes) {
        this.messageService.error(this.translate.instant("notes.delete.insufficientRights"));
      }
    });
  }
}
